import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import { mediamtxService } from '@/mediaservices/mediamtxService';

type Health = 'excellent' | 'good' | 'fair' | 'poor';

interface HostCoHostMetrics {
  streamId: string;
  userId: string;
  deviceGroup: string;
  deviceSerialNumber: string;
  deviceInfo?: {                   // Nested device info object
    deviceName?: string;           // Device name is inside deviceInfo
    userId?: string;
    isHost?: boolean;
    isCoHost?: boolean;
    isParticipant?: boolean;
    streamUrl?: string;
  };
  whepUrl: string;
  bitrate: number | null;         // in kbps
  framerate: number | null;       // in fps
  resolution: string | null;      // e.g., "1920x1080"
  connectedAt: string;            // ISO string
  refreshRate?: number | null;
  type: 'host' | 'co-host';
}

interface StreamQualityIndicatorProps {
  className?: string;
}

/** --- helpers ------------------------------------------------------------- */

function getDisplayName(m: HostCoHostMetrics | null): string {
  if (!m) return '—';
  // Try multiple possible property names for device name
  return m.deviceInfo?.deviceName ||  m.deviceSerialNumber || '—';
}

function parseResolution(res?: string): { w: number; h: number } | null {
  if (!res) return null;
  const [w, h] = res.split('x').map(Number);
  if (Number.isFinite(w) && Number.isFinite(h)) return { w, h };
  return null;
}

function resolutionToQuality(res?: string): 'FHD' | 'HD' | 'SD' | '—' {
  const r = parseResolution(res);
  if (!r) return '—';
  if (r.w >= 1920 && r.h >= 1080) return 'FHD';
  if (r.w >= 1280 && r.h >= 720) return 'HD';
  return 'SD';
}

function computeHealth(m: HostCoHostMetrics | null): Health {
  if (!m) return 'poor';
  const fps = m.framerate ?? 0;
  const br = m.bitrate ?? 0; // kbps
  const r = parseResolution(m.resolution);

  // lightweight heuristic; tweak thresholds as needed
  if (r && r.w >= 1920 && r.h >= 1080 && br >= 4500 && fps >= 60) return 'excellent';
  if (r && r.w >= 1280 && r.h >= 720 && br >= 3000 && fps >= 30) return 'good';
  if (br >= 1500 && fps >= 24) return 'fair';
  return 'poor';
}

function getHealthConfig(health: Health) {
  switch (health) {
    case 'excellent':
      return { color: 'text-success', bgColor: 'bg-success', label: 'Excellent', bars: 4 };
    case 'good':
      return { color: 'text-accent', bgColor: 'bg-accent', label: 'Good', bars: 3 };
    case 'fair':
      return { color: 'text-warning', bgColor: 'bg-warning', label: 'Fair', bars: 2 };
    default:
      return { color: 'text-error', bgColor: 'bg-error', label: 'Poor', bars: 1 };
  }
}

function formatBitrate(kbps?: number | null) {
  if (kbps == null) return '—';
  if (kbps < 10_000) return `${Math.round(kbps)} kbps`;
  return `${(kbps / 1000).toFixed(1)} Mbps`;
}

function formatFps(fps?: number | null) {
  if (fps == null) return '—';
  return `${Math.round(fps)} fps`;
}

function formatConnected(ts?: string) {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
}

/** --- component ----------------------------------------------------------- */

const StreamQualityIndicator: React.FC<StreamQualityIndicatorProps> = ({ className = '' }) => {
  const [host, setHost] = useState<HostCoHostMetrics | null>(null);
  const [coHost, setCoHost] = useState<HostCoHostMetrics | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const handler = (data: any) => {  
      // Defensive: always set type and handle nulls
      if (data?.host) {
        setHost({ ...data.host, type: 'host' });
      } else {
        setHost(null);
      }

      if (Array.isArray(data?.coHosts) && data.coHosts.length > 0) {
        setCoHost({ ...data.coHosts[0], type: 'co-host' });
      } else {
        setCoHost(null);
      }
    };

    mediamtxService.on('host_status_update', handler);
    // ask once for current status if your service exposes it
    mediamtxService.emit?.('host_status_get', {}); // safe no-op if not present

    return () => mediamtxService.off('host_status_update', handler);
  }, []);

  // derive top-line display from host primarily
  const quality = useMemo(() => resolutionToQuality(host?.resolution), [host?.resolution]);
  const hostHealth = useMemo(() => computeHealth(host), [host]);
  const hostHealthCfg = useMemo(() => getHealthConfig(hostHealth), [hostHealth]);

  // for the header, show some quick metrics
  const headerBitrate = useMemo(() => formatBitrate(host?.bitrate), [host?.bitrate]);
  const headerFps = useMemo(() => formatFps(host?.framerate), [host?.framerate]);
  const headerRes = host?.resolution ?? '—';

  const Bars: React.FC<{ bars: number; active: number; activeClass: string }> = ({ bars, active, activeClass }) => (
    <div className="flex space-x-0.5">
      {Array.from({ length: bars }, (_, i) => i + 1).map((bar) => (
        <div
          key={bar}
          className={`w-1 h-3 rounded-sm ${bar <= active ? activeClass : 'bg-muted'}`}
        />
      ))}
    </div>
  );

  const RoleCard: React.FC<{ title: string; m: HostCoHostMetrics | null }> = ({ title, m }) => {
    if (!m) return null;
    const health = computeHealth(m);
    const cfg = getHealthConfig(health);

    return (
      <div className="flex flex-col p-3 bg-card min-w-[220px]">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-foreground">{title} - {getDisplayName(m)}</span>
          <span className={`text-[10px] font-medium px-2 py-0.5 rounded ${cfg.bgColor} text-white`}>
            {cfg.label}
          </span>
        </div>
        <div className="flex flex-col gap-1 text-xs">
          <div><span className="text-muted-foreground">Device Serial Number:</span> <span className="text-foreground">{m.deviceSerialNumber || '—'}</span></div>
          <div><span className="text-muted-foreground">Device Group:</span> <span className="text-foreground">{m.deviceGroup || '—'}</span></div>
          <div><span className="text-muted-foreground">Resolution:</span> <span className="font-mono text-foreground">{m.resolution || '—'}</span></div>
          <div><span className="text-muted-foreground">Frame Rate:</span> <span className="font-mono text-foreground">{formatFps(m.framerate)}</span></div>
          <div><span className="text-muted-foreground">Bitrate:</span> <span className="font-mono text-foreground">{formatBitrate(m.bitrate)}</span></div>
          <div><span className="text-muted-foreground">Refresh Rate:</span> <span className="font-mono text-foreground">{m.refreshRate ?? '—'}</span></div>
          <div><span className="text-muted-foreground">Connected:</span> <span className="text-foreground">{formatConnected(m.connectedAt)}</span></div>
        </div>
      </div>
    );
  };

  if (!host && !coHost) return null;

  return (
    <div className={`relative ${className}`}>
      <div
        role="button"
        tabIndex={0}
        className="flex items-center gap-3 p-3 bg-card border border-border rounded-lg cursor-pointer hover:bg-muted transition-micro"
        onClick={() => setShowDetails((v) => !v)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setShowDetails((v) => !v); }}
      >
        
        {/* Host Crown Icon with Health bars */}
        <div className="flex items-center gap-1.5">
          <Icon name="Crown" size={12} className="text-primary" />
           {getDisplayName(host)}
          <Bars bars={4} active={hostHealthCfg.bars} activeClass={hostHealthCfg.bgColor} />
        </div>

        {/* Co-host indicator with health bars if present */}
        {coHost && (
          <div className="flex items-center gap-1.5">
            <Icon name="UserCheck" size={12} className="text-primary" />
            {getDisplayName(coHost)}
            <Bars bars={4} active={getHealthConfig(computeHealth(coHost)).bars} activeClass={getHealthConfig(computeHealth(coHost)).bgColor} />
          </div>
        )}
        
        <Icon
          name={showDetails ? 'ChevronUp' : 'ChevronDown'}
          size={14}
          className="text-muted-foreground ml-auto"
        />
      </div>

      {/* Detailed Metrics */}
      {showDetails && (
          <div className="flex flex-col border border-border rounded-lg mt-2 overflow-hidden">
            <RoleCard title="Host" m={host} />
            {coHost && (
              <>
                <div className="border-t border-border" />
                <RoleCard title="Co-host" m={coHost} />
              </>
            )}
          </div>
      )}
    </div>
  );
};

export default StreamQualityIndicator;